import { hasConsent } from './cookies/consent.js';
import { CONSENT_EVENT, type CookieConsent } from './cookies/types.js';

/**
 * Google Analytics 4, gated behind cookie consent.
 *
 * - The measurement ID comes from VITE_GA_MEASUREMENT_ID (set per environment
 *   in .env.staging / .env.production; empty in dev = analytics fully off).
 * - gtag.js is only injected AFTER the user accepts cookies, and Consent Mode
 *   v2 defaults everything to "denied" so nothing is stored before consent.
 * - Rejecting consent (banner or cookie settings) revokes consent-mode grants
 *   and removes existing _ga* cookies.
 */

const GA_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined)?.trim() ?? '';

// Guards against placeholder values accidentally left in env files.
const GA_ID_PATTERN = /^G-[A-Z0-9]{4,}$/;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let scriptLoaded = false;
let initialized = false;

function gaConfigured(): boolean {
  return GA_ID_PATTERN.test(GA_ID) && !GA_ID.includes('XXXX');
}

function gtag(..._args: unknown[]) {
  window.dataLayer = window.dataLayer ?? [];
  // GA processes the arguments object, not a plain array — keep this exact shape.
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

function loadGtagScript() {
  if (scriptLoaded) return;
  scriptLoaded = true;

  window.dataLayer = window.dataLayer ?? [];
  window.gtag = gtag;

  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  });
  gtag('js', new Date());
  gtag('config', GA_ID, { send_page_view: false, anonymize_ip: true });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);
}

function grantAnalyticsConsent() {
  loadGtagScript();
  gtag('consent', 'update', { analytics_storage: 'granted' });
  trackPageView(window.location.pathname + window.location.search);
}

function revokeAnalyticsConsent() {
  if (!scriptLoaded) return;
  gtag('consent', 'update', { analytics_storage: 'denied' });
  clearGaCookies();
}

function clearGaCookies() {
  const host = window.location.hostname;
  // _ga cookies are set on the registrable domain — expire them on both the
  // exact host and the dot-prefixed parent so staging.* subdomains are covered.
  const domains = ['', host, `.${host}`, `.${host.split('.').slice(-2).join('.')}`];
  document.cookie
    .split(';')
    .map((c) => c.split('=')[0]?.trim() ?? '')
    .filter((name) => name === '_ga' || name.startsWith('_ga_'))
    .forEach((name) => {
      domains.forEach((domain) => {
        const domainPart = domain ? `; domain=${domain}` : '';
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}`;
      });
    });
}

/** Call once at app start. Safe to call when GA is not configured (no-op). */
export function initAnalytics() {
  if (initialized || typeof window === 'undefined' || !gaConfigured()) return;
  initialized = true;

  if (hasConsent()) {
    grantAnalyticsConsent();
  }

  window.addEventListener(CONSENT_EVENT, (event) => {
    const consent = (event as CustomEvent<CookieConsent>).detail;
    if (consent?.accepted) {
      grantAnalyticsConsent();
    } else {
      revokeAnalyticsConsent();
    }
  });
}

/** Report an SPA navigation. No-op unless GA is configured and consented. */
export function trackPageView(path: string) {
  if (!scriptLoaded || !hasConsent()) return;
  gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

/** Track a custom product event (e.g. simulation_started). Consent-gated. */
export function trackEvent(name: string, params?: Record<string, string | number | boolean>) {
  if (!scriptLoaded || !hasConsent()) return;
  gtag('event', name, params ?? {});
}
